import { useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { useProfile } from '@/lib/profile-context';
import { userQualifiesForAuditCredit } from '@/lib/plan-access';
import { sellersAPI } from '@/lib/api';
import AuditPlanHomeCard from '@/components/billing/AuditPlanHomeCard';
import { toast } from 'sonner';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function resolveSellerId(accountId, profileId, { triggerSync = false } = {}) {
  const sellersRes = await sellersAPI.getAll({
    account_id: accountId,
    profile_id: profileId,
  });
  const sellers = Array.isArray(sellersRes.data) ? sellersRes.data : [];
  const sellerId = sellers[0]?.id;
  if (sellerId || !triggerSync) {
    return sellerId ?? null;
  }

  await sellersAPI.syncByProfile({
    account_id: accountId,
    profile_id: profileId,
  });

  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (attempt > 0) {
      await sleep(1500);
    }
    const retryRes = await sellersAPI.getAll({
      account_id: accountId,
      profile_id: profileId,
    });
    const retrySellers = Array.isArray(retryRes.data) ? retryRes.data : [];
    if (retrySellers[0]?.id) {
      return retrySellers[0].id;
    }
  }

  return null;
}

export default function AuditReportPage() {
  const { user } = useAuth();
  const { accountId, profileId, currentAccount, currentProfile } = useProfile();
  const [downloading, setDownloading] = useState(false);

  const accountLabel = currentAccount?.accountName || currentAccount?.name || (accountId ? `Account ${accountId}` : '');
  const profileLabel = currentProfile?.name || (profileId ? `Profile ${profileId}` : '');
  const country = currentProfile?.countryCode ? ` (${currentProfile.countryCode})` : '';
  const selectedStoreLabel = accountLabel && profileLabel
    ? `${accountLabel} → ${profileLabel}${country}`
    : null;
  const hasSelection = Boolean(accountId && profileId);

  const handleDownload = async () => {
    if (!hasSelection) {
      toast.error('Select an Amazon account and profile first.');
      return;
    }
    setDownloading(true);
    try {
      let sellerId = await resolveSellerId(accountId, profileId);

      if (!sellerId) {
        toast.message('Preparing your audit data from Amazon…', {
          description: 'First sync can take a minute after connecting a new store.',
        });
        sellerId = await resolveSellerId(accountId, profileId, { triggerSync: true });
      }

      if (!sellerId) {
        toast.error('Your audit data is still syncing from Amazon. Please wait 1–2 minutes and try again.');
        return;
      }

      const res = await sellersAPI.downloadAuditReportPdf(sellerId);
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match?.[1] || `Protego-Account-Health-Audit-${new Date().toISOString().split('T')[0]}.pdf`;
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Audit report downloaded');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate audit report');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="audit-report-page">
      <div>
        <h1 className="text-xl font-semibold">Account Health Audit</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Download the audit PDF for the store you selected. Switch stores anytime from Amazon Accounts.
        </p>
      </div>

      <AuditPlanHomeCard
        selectedStoreLabel={selectedStoreLabel}
        hasSelection={hasSelection}
        downloading={downloading}
        onDownloadAudit={handleDownload}
        showAuditCredit={userQualifiesForAuditCredit(user)}
      />
    </div>
  );
}
